/**
 * Shipping methods — what the client posts to the seller-owned
 * `shipping_methods` table via the listings shipping-methods routes.
 *
 * Closing G24 from FRONTEND_WIRING_STRATEGY: `EditShippingTab.tsx`
 * historically did 4 direct `supabase.from('shipping_methods').{insert,
 * update,delete,select}` calls from a React component. This contract
 * pins the wire shape for the new HTTP boundary that replaces those
 * direct DB calls. Same for `components/shipping-settings/*`.
 *
 * `shipping_methods` rows are seller-scoped (joined to a listing via
 * `listings.shipping_config[].shipping_method_id`). The seller owns the
 * method — buyers see it through listings that reference it. RLS gates
 * write access to `auth.uid() = user_id`.
 *
 * Field shapes mirror the existing column types:
 *  - `id`: client-generated uuid (current EditShippingTab pattern, kept
 *    to avoid a server-roundtrip echo on add)
 *  - `name`, `description`: free text
 *  - `price`: stored as string (the column is text/numeric-text in DB,
 *    the form input is a string, no coercion historically)
 *  - `banned_countries`: ISO 3166-1 alpha-2 codes; the route validates
 *    that not ALL PayPal-supported countries are banned (mirrors the
 *    component-level check at `EditShippingTab.tsx:298-301`)
 *  - `currency`: 3-letter currency code (USD/EUR/GBP/...); falls back
 *    to seller default if absent. POST-K.6 (F-05, 2026-06-24) the value is
 *    SERVER-ENFORCED via the `trg_shipping_methods_currency_from_profile`
 *    trigger that overwrites it from `profiles.currency` on every
 *    INSERT/UPDATE. The wire field is kept for back-compat with the
 *    component-level builder, but its value is IGNORED by the DB; the
 *    server-resolved currency is echoed back in the response (see
 *    `AddShippingMethodResponse` / `UpdateShippingMethodResponse`).
 *
 * @see lib/listings/shipping-methods-actions.ts — canonical web builders
 * @see app/api/listings/shipping-methods/route.ts — POST (add) + GET (list)
 * @see app/api/listings/shipping-methods/[id]/route.ts — PATCH + DELETE
 * @see components/my-listings/EditShippingTab.tsx — sole producer today
 *      (closed by Block C of the edit-listing hardening workstream)
 * @see .tondocs/todos/listings/p1-edit-listing-l1-l2-l4-hardening.md
 */

import { z } from "zod";
import { pinned } from "./pin";

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

/**
 * Writable fields on a shipping method. Shared between add (POST) and
 * update (PATCH); add requires all, update permits any subset.
 */
interface ShippingMethodWritable {
  name: string;
  description: string;
  /** Free-form decimal string — DB column is text, the form input is a string. */
  price: string;
  banned_countries: string[];
  /** ISO 4217 (`USD` / `EUR` / `GBP` / `TWD` / `JPY` / `CAD`). */
  currency: string;
}

export interface AddShippingMethodRequest extends ShippingMethodWritable {
  /**
   * Client-generated uuid. Kept on the wire to mirror the existing
   * `EditShippingTab.tsx` pattern (uuidv4 on add) — lets the optimistic
   * UI write to local state with the final id immediately.
   */
  id: string;
}

export interface UpdateShippingMethodRequest {
  name?: string;
  description?: string;
  price?: string;
  banned_countries?: string[];
  currency?: string;
}

// ---------------------------------------------------------------------------
// Zod schemas
// ---------------------------------------------------------------------------

const WritableShape = {
  name: z.string(),
  description: z.string(),
  price: z.string(),
  banned_countries: z.array(z.string()),
  currency: z.string(),
} as const;

const AddSchema = z.object({
  id: z.string().min(1),
  ...WritableShape,
});

const UpdateSchema = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
  price: z.string().optional(),
  banned_countries: z.array(z.string()).optional(),
  currency: z.string().optional(),
});

export const AddShippingMethodRequestSchema = pinned<AddShippingMethodRequest>()(
  AddSchema,
);

export const UpdateShippingMethodRequestSchema =
  pinned<UpdateShippingMethodRequest>()(UpdateSchema);

// ---------------------------------------------------------------------------
// Response shapes — server-resolved currency echo (F-05, 2026-06-24)
// ---------------------------------------------------------------------------
//
// F-05 mirrors F-04 (listings): the `currency` field on the wire is purely
// input-side; the K.6 trigger overwrites it from `profiles.currency` server-
// side. The client must NEVER trust the value it sent — it must read the
// server-resolved value from the response. These echo shapes lock the
// contract.

/**
 * `POST /api/listings/shipping-methods` response. `currency` is the
 * server-resolved value (echoed from the inserted row's `currency` column
 * after the K.6 trigger has applied the profile-pin).
 */
export interface AddShippingMethodResponse {
  id: string;
  currency: string;
}

/**
 * `PATCH /api/listings/shipping-methods/[id]` response. Same echo semantics
 * as the POST response — `currency` is the post-trigger value, regardless
 * of whether the PATCH body carried a `currency` field.
 */
export interface UpdateShippingMethodResponse {
  id: string;
  currency: string;
}

export const AddShippingMethodResponseSchema =
  pinned<AddShippingMethodResponse>()(
    z.object({
      id: z.string(),
      currency: z.string(),
    }),
  );

export const UpdateShippingMethodResponseSchema =
  pinned<UpdateShippingMethodResponse>()(
    z.object({
      id: z.string(),
      currency: z.string(),
    }),
  );
